/**
 * Logic of the bulk RSVP editor (Guests > RSVPs), kept apart from the screen so it can be tested on its own.
 *
 * A guest's answer for one event is one of four states: not invited (null), invited (pending), confirmed, declined.
 * The screen keeps the answers as loaded (the baseline) and, for each guest the user touched, a full draft. Only the
 * events whose answer differs from the baseline are sent. Nothing here changes its inputs.
 */

export type RsvpAnswer = 'pending' | 'confirmed' | 'declined' | null;
/** One guest's answers: event id to answer, null where the guest is not invited. */
export type EventAnswers = Record<string, RsvpAnswer>;
export type RsvpFilter = 'pending' | 'everyone' | 'mine';

/** The API accepts at most this many guests in one bulk call. */
export const MAX_GUESTS_PER_CALL = 200;

const CYCLE: readonly RsvpAnswer[] = [null, 'pending', 'confirmed', 'declined'];

/** not invited, then invited, confirmed, declined, then back to not invited. */
export function nextAnswer(current: RsvpAnswer): RsvpAnswer {
  return CYCLE[(CYCLE.indexOf(current) + 1) % CYCLE.length];
}

export function answerLabel(answer: RsvpAnswer): string {
  if (answer === 'pending') return 'Invited';
  if (answer === 'confirmed') return 'Confirmed';
  if (answer === 'declined') return 'Declined';
  return 'Not invited';
}

/** One answer from the project-wide read. */
export interface BaselineRow { guest_ref: string; event_id: string; rsvp_status: string }

/** What the server holds per guest: only the invited events. */
export type Baseline = Map<string, EventAnswers>;
/** Each guest's version stamp as the server sent it (ISO, milliseconds), by guest ref. */
export type Stamps = Map<string, string>;

function asAnswer(status: string): Exclude<RsvpAnswer, null> {
  return status === 'confirmed' || status === 'declined' ? status : 'pending';
}

export function baselineFrom(rows: readonly BaselineRow[]): Baseline {
  const out: Baseline = new Map();
  for (const r of rows) out.set(r.guest_ref, { ...(out.get(r.guest_ref) ?? {}), [r.event_id]: asAnswer(r.rsvp_status) });
  return out;
}

/** The stamps of the project-wide read: guest ref to the version the bulk save checks `loaded_updated_at` against. */
export function stampsFrom(guests: ReadonlyArray<{ guest_ref: string; updated_at: string }>): Stamps {
  return new Map(guests.map(g => [g.guest_ref, g.updated_at]));
}

/** One entry per project event, null where the guest is not invited. */
export function answersFor(invited: EventAnswers | undefined, eventIds: readonly string[]): EventAnswers {
  const out: EventAnswers = {};
  for (const id of eventIds) out[id] = invited?.[id] ?? null;
  return out;
}

/** Only the events whose answer differs (a removal is null). */
export function changedEvents(base: EventAnswers, draft: EventAnswers): Record<string, RsvpAnswer> {
  const out: Record<string, RsvpAnswer> = {};
  for (const id of Object.keys(draft)) if ((base[id] ?? null) !== (draft[id] ?? null)) out[id] = draft[id] ?? null;
  return out;
}

// ── quick actions: each returns a new set of answers ──

/** Invites the guest to every event they are not invited to yet. Answers already given stay. */
export function inviteAll(answers: EventAnswers, eventIds: readonly string[]): EventAnswers {
  const out: EventAnswers = { ...answers };
  for (const id of eventIds) out[id] = answers[id] ?? 'pending';
  return out;
}

/** Invites the guest to every event except the chosen one, which becomes not invited. */
export function inviteAllBut(answers: EventAnswers, eventIds: readonly string[], exceptEventId: string): EventAnswers {
  const out = inviteAll(answers, eventIds);
  out[exceptEventId] = null;
  return out;
}

/** Confirms every invited (pending) event. Declined and not-invited events stay as they are. */
export function confirmInvited(answers: EventAnswers): EventAnswers {
  const out: EventAnswers = {};
  for (const id of Object.keys(answers)) out[id] = answers[id] === 'pending' ? 'confirmed' : answers[id];
  return out;
}

// ── small helpers ──

/** The last four digits of a phone number; the whole number when it has fewer. Empty without a phone. */
export function lastFour(phone: string | null | undefined): string {
  return (phone ?? '').replace(/\D/g, '').slice(-4);
}

export function chunk<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

// ── what gets sent ──

export interface BulkChange { guest_id: string; loaded_updated_at?: string; events: Record<string, RsvpAnswer> }

interface Ctx {
  baseline: Baseline;
  drafts: Readonly<Record<string, EventAnswers>>;
  removed: ReadonlySet<string>;
  eventIds: readonly string[];
}

export function buildBulkChanges(
  input: Ctx & { guests: ReadonlyArray<{ id: string }>; stamps: Stamps },
): BulkChange[] {
  const out: BulkChange[] = [];
  for (const g of input.guests) {
    const draft = input.drafts[g.id];
    if (!draft || input.removed.has(g.id)) continue;
    const events = changedEvents(answersFor(input.baseline.get(g.id), input.eventIds), draft);
    if (Object.keys(events).length === 0) continue;
    const at = input.stamps.get(g.id);
    out.push({ guest_id: g.id, ...(at ? { loaded_updated_at: at } : {}), events });
  }
  return out;
}

export function guestIsChanged(guestId: string, ctx: Ctx): boolean {
  if (ctx.removed.has(guestId)) return true;
  const draft = ctx.drafts[guestId];
  return !!draft && Object.keys(changedEvents(answersFor(ctx.baseline.get(guestId), ctx.eventIds), draft)).length > 0;
}

/** Guests with a real change, plus guests staged for removal. Each counts once. */
export function countChangedGuests(ctx: Ctx): number {
  const ids = new Set([...Object.keys(ctx.drafts), ...ctx.removed]);
  let n = 0;
  for (const id of ids) if (guestIsChanged(id, ctx)) n += 1;
  return n;
}

/** Pending follows what was loaded (not the draft), so a card does not vanish from the list while it is being edited. */
export function guestMatchesFilter(filter: RsvpFilter, guestId: string, ctx: Ctx): boolean {
  if (filter === 'everyone') return true;
  if (filter === 'mine') return guestIsChanged(guestId, ctx);
  return Object.values(ctx.baseline.get(guestId) ?? {}).some(a => a === 'pending');
}

/** "n of m events confirmed": confirmed events against the events the guest is invited to. For display only. */
export function confirmedSummary(answers: EventAnswers): { confirmed: number; invited: number } {
  const values = Object.values(answers).filter((a): a is Exclude<RsvpAnswer, null> => a !== null);
  return { confirmed: values.filter(a => a === 'confirmed').length, invited: values.length };
}
